import { useEffect, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { api } from 'core/api/ConnectionManager'
import deepEqual from 'fast-deep-equal'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import { FilterQuery } from '../Filter/Filter-types'
import { ISearchbarLegacyFilter } from '../searchbar'

export interface ISearchbarFilterOption {
    id: string
    name: string
}

export interface ISearchbarFilterField extends ISearchbarFilterOption {
    index: number
    checked: boolean
}

type SelectedFilters = Record<string, string[]>

interface IFilterValueObject {
    id?: string
    name: string
    checked?: boolean
}

type FilterValue = string | IFilterValueObject
type FilterValuesResponse = Record<string, FilterValue[]>

interface IState {
    isFilterOpen: boolean
    selectedFilter: ISearchbarFilterOption | null
    filterFields: ISearchbarFilterField[]
    filterSearch: string
    selected: SelectedFilters
}

export interface ISearchbarFilterOwnProps {
    filter: ISearchbarLegacyFilter
    onFilterChange?: (filter: FilterQuery) => void
    mod?: string
}

export type ISearchbarFilterProps = ISearchbarFilterOwnProps & RouteComponentProps

const SearchbarFilterMethods = (p_: ISearchbarFilterProps) => {
    const [state, setState] = useState<IState>({
        isFilterOpen: false,
        selectedFilter: null,
        filterFields: [],
        filterSearch: '',
        selected: { ...p_.filter.selected },
    })
    const previousFilter = useRef(p_.filter)

    useEffect(() => {
        if (!deepEqual(previousFilter.current, p_.filter)) {
            setState((prevState) => ({ ...prevState, selected: { ...p_.filter.selected } }))
        }

        previousFilter.current = p_.filter
    }, [p_.filter])

    const filterSearch = (filterSearch: string) => {
        setState((prevState) => ({ ...prevState, filterSearch }))
    }
    const openFilter = () => {
        setState((prevState) => ({
            ...prevState,
            isFilterOpen: true,
            selectedFilter: null,
            selected: { ...p_.filter.selected },
        }))
    }
    const closeFilter = () => {
        setState((prevState) => ({ ...prevState, isFilterOpen: false }))
    }
    const selectFilter = (selectedFilter: ISearchbarFilterOption) => {
        const selected = state.selected
        const locationQuery = getURLSearchParamsByLocation(p_.location)
        const query = locationQuery.query

        setState((prevState) => ({ ...prevState, selectedFilter, filterFields: [], filterSearch: '' }))

        api.send<object, FilterValuesResponse>('getFilterValues', {
            query,
            groupId: p_.filter.groupId,
            [selectedFilter.id]: [],
        }).then((data) => {
            const filterFields = data[selectedFilter.id].map<ISearchbarFilterField>((item, index) => {
                const value = typeof item === 'string' ? item : item.name

                return {
                    id: value,
                    name: value,
                    index,
                    checked: selected[selectedFilter.id]?.includes(value) ?? false,
                }
            })

            setState((prevState) => ({ ...prevState, filterFields }))
        })
    }
    const setSelected = (currentState: IState): IState => {
        if (!currentState.selectedFilter) {
            return currentState
        }

        const selectedForField = currentState.filterFields.filter((field) => field.checked).map((field) => field.id)

        return {
            ...currentState,
            selected: {
                ...currentState.selected,
                [currentState.selectedFilter.id]: selectedForField,
            },
        }
    }
    const checkAll = (checked: boolean) => {
        setState((prevState) =>
            setSelected({
                ...prevState,
                filterFields: prevState.filterFields.map((field) => ({ ...field, checked })),
            })
        )
    }
    const toggleField = (index: number) => {
        setState((prevState) =>
            setSelected({
                ...prevState,
                filterFields: prevState.filterFields.map((field) =>
                    field.index === index ? { ...field, checked: !field.checked } : field
                ),
            })
        )
    }
    const onFilterChange = () => {
        const filter = Object.entries(state.selected).reduce<FilterQuery>((result, [key, values]) => {
            result[key] = values.join('//')
            return result
        }, {})

        closeFilter()
        p_.onFilterChange?.(filter)
    }

    return {
        state,
        onFilterChange,
        toggleField,
        checkAll,
        closeFilter,
        openFilter,
        selectFilter,
        filterSearch,
    }
}

export default SearchbarFilterMethods
